import PropTypes from 'prop-types';
import { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getAppConfig, message as messageApi } from '@icp/settings';
import { parseJSON, isMobile } from '@icp/utils';
import { AIAgentFloatButton, AIAgentV2, Loading } from '@icp/components';
import { translateEntireObj } from '@icp/i18n';
import markdownCodeComponents from './markdownCodeComponents';
import { handleSave } from './markdownCodeComponents/utils';
import { useCurrentPageForm } from './CurrentPageFormStoreProvider';
import { loadFormEntity, loadPage } from './markdownCodeComponents/formEntityLoader';
import restApi from './services/restApi';
import { registerAIAgentHandler, unregisterAIAgentHandler } from './aiAgentBridge';

const ACTION_PROMPTS = {
  OPEN_FORM: '// action:open_form',
  OPEN_PAGE: '// action:open_page',
};

const greetingTaskCache = { promise: null, result: null };
function loadGreetings() {
  if (greetingTaskCache.result) return greetingTaskCache.result;
  if (greetingTaskCache.promise) throw greetingTaskCache.promise;
  greetingTaskCache.promise = restApi
    .get('/flow/api/flow-rest/general-ai-agent/greeting', {
      skipResponseInterceptors: true,
    })
    .then((res) => res.results || [])
    .catch(() => [])
    .then((greetings) => {
      translateEntireObj(greetings);
      greetingTaskCache.result = greetings;
    });
  throw greetingTaskCache.promise;
}

function getDynamicData(formApi) {
  const tables = {};
  for (const [key, api] of Object.entries(formApi.getFieldApis())) {
    if (['EditableTable', 'Table'].includes(api.component)) {
      tables[key] = api;
    }
  }
  const asyncData = {};
  return Promise.all(
    Object.entries(tables).map(([key, api]) => {
      return new Promise((resolve) => {
        const existData = api.getRowData();
        // 如果表格已经有数据了，直接使用，因为表格的 dataFetched 事件有可能优先于 form 的 ready 事件触发
        // 表格在 serverside lazyloading 的时候会有一条空的 [{}] 数据
        if (existData?.length && Object.keys(existData[0]).length !== 0) {
          asyncData[key] = existData;
          resolve();
        } else if (api.component === 'EditableTable') {
          // 对 EditableTable 来说，一般是在 edit 页面才会去 fetch 数据，它自带生成 dataUrl 的逻辑，所以逻辑和普通 Table 表格不一致。
          if (api.needFetchData) {
            api.on('dataFetched', (data) => {
              asyncData[key] = data;
              resolve();
            });
          } else {
            asyncData[key] = api.getComponentData();
            resolve();
          }
        } else if (api.component === 'Table') {
          // 否则则等待数据请求结果
          const { dataSource, dataUrl } = api.getComponentProps();
          if (dataSource || dataUrl) {
            api.on('dataFetched', (data) => {
              asyncData[key] = data;
              resolve();
            });
          }
        } else {
          resolve();
        }
      });
    }),
  ).then(() => {
    return asyncData;
  });
}

function getFormPageRule(pbcToken, formEntityToken, schemaId) {
  const ruleType = formEntityToken ? 'form' : 'page';
  const pbcAndTargetRuleToken = formEntityToken
    ? `${pbcToken}:${formEntityToken}`
    : `${pbcToken}:${schemaId}`;

  return restApi
    .get('/flow/api/flow-rest/system-form-page-rule-flow', {
      params: {
        ruleType,
        pbcAndTargetRuleToken,
      },
    })
    .then((res) => {
      if (res.hasRule) {
        return res.rule;
      }
      return Promise.reject(new Error('no rule found'));
    });
}

const GeneralAIAgent = forwardRef(({ suppressFloatButton, eventHandlers, ...otherProps }, ref) => {
  const { t } = useTranslation();
  const location = useLocation();

  const buttonRef = useRef();
  const aiAgentRef = useRef();
  const aiAgentDelegates = useRef({});
  const currentPageForm = useCurrentPageForm();

  const greetings = loadGreetings();

  useEffect(() => {
    if (!currentPageForm) return () => {};

    let leaved = false;
    const listener = () => {
      if (!buttonRef.current?.open) return;
      const {
        context: { pbcToken, formEntityToken, schemaId },
        formData: { values },
      } = currentPageForm.store.getState();

      Promise.all([
        getFormPageRule(pbcToken, formEntityToken, schemaId),
        getDynamicData(currentPageForm.formApi),
      ])
        .then(([rule, dynamicData]) => {
          if (leaved) return;

          const formEntityData = { formEntityDataId: values.id, ...values };
          for (const [key, value] of Object.entries(dynamicData)) {
            formEntityData[key] = value;
          }

          const newUserContext =
            pbcToken && (formEntityToken || schemaId)
              ? {
                  pbcToken,
                  formEntityToken,
                  pageToken: schemaId,
                  formEntityData,
                }
              : null;
          aiAgentDelegates.current.getUserContext = () => newUserContext;
          sendUserContextMessage(rule);
        })
        .catch((err) => {
          if (err.message !== 'no rule found') return Promise.reject(err);
          return null;
        });
    };

    currentPageForm.formApi.on('ready', listener);
    if (currentPageForm.formApi.stage === 'ready') {
      listener();
    }
    return () => {
      leaved = true;
      currentPageForm.formApi.off('ready', listener);
    };
  }, [currentPageForm]);

  const onCustomSSEMessage = (message) => {
    if (/^FORM_AUTOFILL$/i.test(message.event)) {
      const data = parseJSON(parseJSON(parseJSON(message.data)?.data)?.form_data);
      if (data) {
        currentPageForm?.formApi?.setData(data);
      }
    }
  };

  const promptAnswerActions = (msgContent) =>
    [
      /```(icp-table|icp-chart|icp-generic-chart)/.test(msgContent || '') && {
        label: t('save', { ns: 'icp-common' }),
        handlerKey: 'onSave',
      },
    ].filter(Boolean);

  const sendUserContextMessage = (rule) => {
    if (!buttonRef.current?.open) return;
    const userContext = aiAgentDelegates.current?.getUserContext?.();
    if (!userContext?.pbcToken || (!userContext?.formEntityToken && !userContext?.pageToken)) {
      return;
    }
    const prefix = userContext.formEntityToken
      ? ACTION_PROMPTS.OPEN_FORM
      : ACTION_PROMPTS.OPEN_PAGE;
    const message = `${prefix}\n${JSON.stringify(userContext)}`;
    aiAgentRef.current?.sendMessage(message, [], { ...userContext, rule });
  };

  useImperativeHandle(ref, () => ({
    get sendMessage() {
      return aiAgentRef.current?.sendMessage;
    },
  }));

  // 注册到 bridge，让 GlobalSearch 等跨组件树的调用方可以打开面板并选中 agent
  useEffect(() => {
    registerAIAgentHandler({
      openWithAgent(agentId, prefillText) {
        // flushSync 强制 React 同步完成 setOpen(true) 的渲染，
        // 确保 AIAgentV2 从 isHidden=true 变为 false 并挂载，aiAgentRef 有值后再调用
        flushSync(() => {
          buttonRef.current?.openPanel();
        });
        aiAgentRef.current?.openWithAgent(agentId, prefillText);
      },
    });
    return () => {
      unregisterAIAgentHandler();
    };
  }, []);

  if (location.pathname.startsWith('/mobile/system-ai-agent')) return null;
  if (getAppConfig()?.agent?.showFloatButton === 'hide') return null;

  const agentElement = (
    <AIAgentV2
      ref={aiAgentRef}
      greetings={greetings}
      markdownCodeComponents={markdownCodeComponents}
      eventHandlers={{
        onSave: (msg, question) =>
          handleSave(msg, currentPageForm?.formApi, question).then(() => {
            messageApi.success(t('chat.save-portlet-success', { ns: 'default' }));
          }),
        onCustomSSEMessage,
        renderPromptText,
        ...eventHandlers,
      }}
      promptAnswerActions={promptAnswerActions}
      {...otherProps}
    />
  );

  if (suppressFloatButton) return agentElement;

  const defaultOpen = isMobile()
    ? window.__bot_conf__?.mobileSystemAgentDefaultOpen ??
      window.__bot_conf__?.systemAgentDefaultOpen
    : window.__bot_conf__?.systemAgentDefaultOpen;

  return (
    <AIAgentFloatButton ref={buttonRef} defaultOpen={defaultOpen}>
      {agentElement}
    </AIAgentFloatButton>
  );
});

GeneralAIAgent.propTypes = {
  suppressFloatButton: PropTypes.bool,
  eventHandlers: PropTypes.shape({}),
};

export default GeneralAIAgent;

function FormEntityName(props) {
  // eslint-disable-next-line react/prop-types
  const { pbcToken, formEntityToken } = props;
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formEntity, setFormEntity] = useState(null);

  useEffect(() => {
    if (!pbcToken || !formEntityToken) return;
    setLoading(true);
    loadFormEntity(pbcToken, formEntityToken)
      .then((data) => {
        translateEntireObj(data);
        setFormEntity(data);
      })
      .finally(() => setLoading(false));
  }, [pbcToken, formEntityToken]);

  if (loading) return <Loading />;

  const formName = formEntity?.name || formEntityToken;
  return t('ai.event.open-form', { form: formName });
}
function PageName(props) {
  // eslint-disable-next-line react/prop-types
  const { pbcToken, pageToken } = props;
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(null);

  useEffect(() => {
    if (!pbcToken || !pageToken) return;
    setLoading(true);
    loadPage(pbcToken, pageToken)
      .then((data) => {
        translateEntireObj(data);
        setPage(data);
      })
      .finally(() => setLoading(false));
  }, [pbcToken, pageToken]);

  if (loading) return <Loading />;

  const pageName = page?.name || pageToken;
  return t('ai.event.open-page', { page: pageName });
}

export const renderPromptText = (text) => {
  if (text.startsWith(ACTION_PROMPTS.OPEN_FORM)) {
    const userContext = JSON.parse(text.replace(ACTION_PROMPTS.OPEN_FORM, ''));
    return <FormEntityName {...userContext} />;
  }
  if (text.startsWith(ACTION_PROMPTS.OPEN_PAGE)) {
    const userContext = JSON.parse(text.replace(ACTION_PROMPTS.OPEN_PAGE, ''));
    return <PageName {...userContext} />;
  }
  return text;
};
